import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Paper, Typography, Stack, Chip, Button, IconButton,
  TextField, Divider, CircularProgress, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  InputAdornment, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Close, Medication as MedicationIcon, Search, AccessTime,
  CheckCircle, EventAvailable, Inventory2, ScheduleSend,
  WarningAmberRounded, Today,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import moment from 'moment';
import {
  saveChartedMedicationdb,
} from '../BaseURL';
import {
  frequencyMap, frequencyMultiplier, resolveDoseOffsets,
} from '../Message';

// ── Theme tokens ──────────────────────────────────────────────────────
const PRIMARY      = '#1A9B7F';
const PRIMARY_DARK = '#157763';
const PRIMARY_BG   = 'rgba(26,155,127,0.08)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';

const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

// ── Frequency math (kept identical to InPatient.js) ───────────────────
const resolveDosesPerDay = (rawFreq) => {
  const norm = String(rawFreq || '').toLowerCase().trim();
  const canonical = frequencyMap[norm] || rawFreq;
  const n = frequencyMultiplier[canonical];
  if (typeof n === 'number') return n;
  return 1;
};

const buildSchedule = (medication, firstDoseDate, firstDoseTime, patient, currentUser, chartMode = 'quantity') => {
  if (!medication || !firstDoseDate || !firstDoseTime) return [];

  const freq = String(medication.freq || '').toLowerCase().trim();
  const dosesPerDay = resolveDosesPerDay(medication.freq);
  const medDays = Math.max(1, parseInt(medication.day_server, 10) || 1);
  const qty = parseInt(medication.qua, 10);
  // In 'frequency' mode we intentionally ignore the dispensed quantity —
  // one bottle of syrup can carry many doses, so capping by qty would
  // silently under-schedule. Quantity mode keeps the old cap behaviour.
  const capByQuantity = chartMode !== 'frequency';
  const quantity = (capByQuantity && Number.isFinite(qty) && qty > 0) ? qty : Number.POSITIVE_INFINITY;
  // Strict parse so a stray AM/PM or non-HH:mm value can't produce an
  // "Invalid date" that silently formats back to the same anchor on every
  // iteration — that was the symptom users reported: "the next day's dose
  // shows the current day". Bail with [] if the parse is unusable; the host
  // dialog already disables the Chart button when schedule.length === 0.
  const firstDose = moment(`${firstDoseDate} ${firstDoseTime}`, 'YYYY-MM-DD HH:mm', true);
  if (!firstDose.isValid()) return [];

  const isPrn  = dosesPerDay === 0 || /\bprn\b|as needed|if necessary|sos/i.test(freq);
  const isStat = /\bstat\b|^immediately$|^now$/i.test(freq);

  // Explicit hour-offsets take priority — they encode irregular regimens
  // (e.g. artesunate "At 0, 12 and 24 hours") that a flat 24/dosesPerDay
  // interval would mis-space as 8-hourly.
  const explicitOffsets = (isPrn || isStat) ? null : resolveDoseOffsets(medication.freq);

  let totalDoses, intervalHours;
  if (isPrn || isStat) {
    totalDoses = 1; intervalHours = 0;
  } else if (explicitOffsets) {
    totalDoses = explicitOffsets.length;
    intervalHours = 0;
  } else {
    intervalHours = 24 / dosesPerDay;
    totalDoses = Math.max(1, Math.floor(medDays * dosesPerDay));
  }
  totalDoses = Math.min(totalDoses, quantity);

  const charts = [];
  for (let i = 0; i < totalDoses; i++) {
    let t;
    if (isPrn || isStat) t = moment(firstDose);
    else if (explicitOffsets) t = moment(firstDose).add(explicitOffsets[i], 'hours');
    else t = moment(firstDose).add(i * intervalHours, 'hours');
    const medDescription = `${medication.name} ${medication.dose || ''} ${medication.Dose_Unit || ''} ${medication.freq} ${medication.rou || ''}`.replace(/\s+/g, ' ').trim();
    charts.push({
      id:    patient?.id,
      dat:   t.format('MM/DD'),
      time:  t.format('hh:mm:ss A'),
      due_at: t.format('YYYY-MM-DD HH:mm:ss'),
      med:   medDescription,
      name:  patient?.fullName,
      age:   patient?.age,
      d_dat: t.format('YYYY-MM-DD'),
      s_by:  'i',
      user:  currentUser,
      trans_code: medication.trans_code,
      medication_trans_code: medication.trans_code,
    });
  }
  return charts;
};

/**
 * Shared medication charting dialog. Used by both Wards/InPatient.js and
 * Emergency/Wards/InPatient.js. Renders a clean two-column layout:
 *
 *   Left  → searchable list of available medications, grouped by request date.
 *   Right → selected medication's prescription summary, first-dose pickers,
 *           and a deterministic schedule preview (so the nurse sees the
 *           exact times BEFORE clicking Chart).
 *
 * Props:
 *   open, onClose
 *   patient            { id, fullName, age, gender, visitDate }
 *   medications        [{ trans_code, name, dose, Dose_Unit, freq, rou, day_server, qua, dat }]
 *   chartedMedications [{ med }]                used to mark "Already charted"
 *   currentUser        string                   set on each chart row
 *   onSaved()          fired after a successful save so the parent can refetch
 */
export default function MedicationChartDialog({
  open, onClose, patient,
  medications = [], chartedMedications = [],
  currentUser = '', onSaved,
}) {
  const [search, setSearch]               = useState('');
  const [selectedMed, setSelectedMed]     = useState(null);
  const [firstDoseDate, setFirstDoseDate] = useState(moment().format('YYYY-MM-DD'));
  const [firstDoseTime, setFirstDoseTime] = useState(moment().format('HH:mm'));
  const [chartMode, setChartMode]         = useState('quantity');
  const [saving, setSaving]               = useState(false);

  // Reset every time the dialog opens.
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedMed(null);
      setFirstDoseDate(moment().format('YYYY-MM-DD'));
      setFirstDoseTime(moment().format('HH:mm'));
      setChartMode('quantity');
    }
  }, [open]);

  // ── Derived: filterable medication list grouped by request date ──────
  const availableMeds = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (medications || [])
      .filter(m => parseInt(m.qua, 10) > 0)
      .filter(m => !q || (m.name || '').toLowerCase().includes(q));
  }, [medications, search]);

  const groupedMeds = useMemo(() => {
    const groups = {};
    availableMeds.forEach(m => {
      const d = m.dat || 'Unknown';
      (groups[d] = groups[d] || []).push(m);
    });
    return Object.entries(groups).sort(([a], [b]) => moment(b).diff(moment(a)));
  }, [availableMeds]);

  const isChartedFor = (med) => chartedMedications.some(
    c => (c.med || '').toLowerCase().includes((med.name || '').toLowerCase())
  );

  // ── Derived: schedule preview for the selected medication ────────────
  const schedule = useMemo(
    () => buildSchedule(selectedMed, firstDoseDate, firstDoseTime, patient, currentUser, chartMode),
    [selectedMed, firstDoseDate, firstDoseTime, patient, currentUser, chartMode]
  );

  const dosesPerDay = selectedMed ? resolveDosesPerDay(selectedMed.freq) : 0;
  const dosesPerDayLabel = (() => {
    if (!selectedMed)  return '—';
    if (dosesPerDay === 0) return 'PRN';
    if (dosesPerDay < 1)   return dosesPerDay.toFixed(2).replace(/\.?0+$/, '') + '×';
    return `${dosesPerDay}× / day`;
  })();

  const totalEntries = schedule.length;
  const quantity = selectedMed ? (parseInt(selectedMed.qua, 10) || 0) : 0;
  const insufficient = chartMode === 'quantity' && selectedMed && quantity > 0 && totalEntries > 0 && (() => {
    // Compute the "ideal" total before quantity capping, to flag when capped.
    const freq = String(selectedMed.freq || '').toLowerCase().trim();
    const dpd  = resolveDosesPerDay(selectedMed.freq);
    const days = Math.max(1, parseInt(selectedMed.day_server, 10) || 1);
    const isPrn  = dpd === 0 || /\bprn\b|as needed|if necessary|sos/i.test(freq);
    const isStat = /\bstat\b|^immediately$|^now$/i.test(freq);
    const explicitOffsets = (isPrn || isStat) ? null : resolveDoseOffsets(selectedMed.freq);
    const ideal = (isPrn || isStat)
      ? 1
      : (explicitOffsets ? explicitOffsets.length : Math.max(1, Math.floor(days * dpd)));
    return ideal > quantity;
  })();

  // ── Save ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedMed) {
      Swal.fire('Pick a medication', 'Select one of the medications on the left.', 'info');
      return;
    }
    if (quantity <= 0) {
      Swal.fire('No quantity', 'This medication has no quantity available. Please dispense first.', 'warning');
      return;
    }

    if (isChartedFor(selectedMed)) {
      const ok = await Swal.fire({
        title: 'Chart again?',
        html: `<div style="text-align:left;color:#475569;font-size:0.92rem;line-height:1.55">
                 <strong>${selectedMed.name}</strong> already has chart entries for this patient.<br/><br/>
                 Continue only if this is a new course, a different schedule, or a PRN top-up.
               </div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Chart Anyway',
        confirmButtonColor: PRIMARY,
        focusCancel: true,
      });
      if (!ok.isConfirmed) return;
    }

    setSaving(true);
    try {
      const charts = schedule;
      if (!charts.length) {
        Swal.fire('Nothing to chart', 'No schedule entries were generated. Check the prescription.', 'error');
        setSaving(false);
        return;
      }

      const res = await axios.post(saveChartedMedicationdb, {
        charts,
        medicationTransCode: selectedMed.trans_code,
      });

      if (res.data?.success) {
        await Swal.fire({
          title: 'Charted',
          text: `${charts.length} dose${charts.length === 1 ? '' : 's'} scheduled.`,
          icon: 'success',
          confirmButtonColor: PRIMARY,
          timer: 1500,
          showConfirmButton: false,
        });
        if (typeof onSaved === 'function') onSaved();
        onClose && onClose();
      } else {
        throw new Error(res.data?.message || 'Failed to chart medication');
      }
    } catch (err) {
      console.error('chart medication error', err);
      Swal.fire('Error', err.response?.data?.message || err.message || 'Failed to chart medication', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '88vh' } }}
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
        bgcolor: PRIMARY, color: 'white',
      }}>
        <MedicationIcon />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>
            Medication Chart
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
            {patient?.fullName ? `${patient.fullName} · ID ${patient.id}` : 'Select a patient'}
            {patient?.age != null ? ` · ${patient.age} yrs` : ''}
            {patient?.gender ? ` · ${patient.gender}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#f8fafc', display: 'flex', overflow: 'hidden' }}>
        {/* ── Left rail: medications list ──────────────────────────── */}
        <Box sx={{
          width: { xs: '100%', md: 420 },
          minWidth: { md: 380 },
          borderRight: { md: `1px solid ${BORDER}` },
          display: 'flex', flexDirection: 'column',
          bgcolor: '#fff',
        }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ ...LABEL_SX, mb: 1 }}>Available Medications</Typography>
            <TextField
              fullWidth size="small"
              placeholder="Search by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
            {groupedMeds.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: MUTED }}>
                <MedicationIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: '0.9rem' }}>
                  No medications with available quantity.
                </Typography>
              </Box>
            ) : groupedMeds.map(([date, meds]) => (
              <Box key={date} sx={{ mb: 2 }}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1, mb: 0.75,
                  pl: 0.5,
                }}>
                  <Today sx={{ fontSize: 14, color: MUTED }} />
                  <Typography sx={{ ...LABEL_SX }}>
                    {moment(date).isValid() ? moment(date).format('ddd, D MMM YYYY') : date}
                  </Typography>
                  <Chip size="small" label={meds.length}
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#f1f5f9', color: MUTED }} />
                </Box>

                <Stack spacing={0.75}>
                  {meds.map(m => {
                    const charted = isChartedFor(m);
                    const active = selectedMed?.trans_code === m.trans_code;
                    return (
                      <Paper
                        key={m.trans_code}
                        elevation={0}
                        onClick={() => setSelectedMed(m)}
                        sx={{
                          p: 1.25, cursor: 'pointer',
                          border: `1px solid ${active ? PRIMARY : BORDER}`,
                          borderLeft: `3px solid ${active ? PRIMARY : (charted ? '#16a34a' : 'transparent')}`,
                          borderRadius: 2,
                          bgcolor: active ? PRIMARY_BG : '#fff',
                          transition: 'all .15s ease',
                          '&:hover': { borderColor: PRIMARY, bgcolor: active ? PRIMARY_BG : '#f8fafc' },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Typography noWrap sx={{ fontSize: '0.92rem', fontWeight: 700, color: STRONG }}>
                                {m.name}
                              </Typography>
                              {charted && (
                                <Chip size="small" label="Charted"
                                  sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                        bgcolor: 'rgba(22,163,74,0.10)', color: '#15803D' }} />
                              )}
                              {/*  ── DISPENSED, BUT NOT PAID FOR ───────────────
                                   These lines are here because the pharmacy is
                                   allowed to serve before payment, and the ward
                                   has to be able to chart what the patient was
                                   actually given. The endpoint admits them on
                                   `Serve = 1` rather than on payment.

                                   Marked, not hidden. It is a real medication
                                   that is genuinely outstanding at the cash
                                   desk, and a nurse charting it should be able
                                   to see that without it stopping them.

                                   `isPaid === false` and not `!m.isPaid`: an
                                   older server does not send the field at all,
                                   and undefined must read as "not stated"
                                   rather than as "unpaid" on every row.  */}
                              {m.isPaid === false && (
                                <Tooltip title={
                                  `Dispensed but not paid for${m.paymentStatus
                                    ? ` — payment status: ${m.paymentStatus}` : ''}.`
                                  + ' It can still be charted and administered.'}>
                                  <Chip size="small" label="Unpaid"
                                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                          bgcolor: 'rgba(180,83,9,0.10)', color: '#B45309' }} />
                                </Tooltip>
                              )}
                              {/*  ── THE OTHER HALF: PAID FOR, NOT YET HANDED OVER ──
                                   The list admits these on payment, which is how
                                   it has always worked and is right — a ward
                                   writes the schedule up before the trolley
                                   arrives, and the dose times do not depend on
                                   where the box is.

                                   What must not happen is a nurse marking a dose
                                   SERVED for something that never left the
                                   dispensary. Charting is scheduling; giving is
                                   the separate action. So it is charted freely
                                   and labelled honestly.

                                   `=== false` for the same reason as Unpaid: an
                                   older server omits the field, and undefined
                                   must not paint every row undispensed.       */}
                              {m.served === false && (
                                <Tooltip title={'Paid for but not yet dispensed by the pharmacy.'
                                  + ' It can be charted now — confirm the drug is on the ward'
                                  + ' before marking a dose as given.'}>
                                  <Chip size="small" label="Not dispensed"
                                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                          bgcolor: 'rgba(71,85,105,0.10)', color: '#475569' }} />
                                </Tooltip>
                              )}
                            </Stack>
                            <Typography noWrap sx={{ fontSize: '0.75rem', color: MUTED, mt: 0.25 }}>
                              {m.dose} {m.Dose_Unit} · {m.freq} · {m.rou}
                            </Typography>
                          </Box>
                          <Stack alignItems="flex-end" spacing={0.25}>
                            <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>Qty</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: STRONG }}>
                              {m.qua}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Right pane: detail + schedule preview ────────────────── */}
        <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', overflow: 'hidden' }}>
          {!selectedMed ? (
            <Box sx={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', color: MUTED,
              p: 4,
            }}>
              <ScheduleSend sx={{ fontSize: 64, color: '#cbd5e1', mb: 1.5 }} />
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: STRONG }}>
                Select a medication
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', mt: 0.5, textAlign: 'center', maxWidth: 360 }}>
                Pick a prescription on the left, set the first dose time,
                and the dosing schedule will be calculated automatically.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2.5, overflow: 'auto', flex: 1 }}>
                {/* Prescription summary card */}
                <Paper elevation={0} sx={{ p: 2, border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff' }}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: STRONG }}>
                    {selectedMed.name}
                  </Typography>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 1.25, mt: 1.25,
                  }}>
                    <SummaryCell label="Dose"      value={`${selectedMed.dose || '—'} ${selectedMed.Dose_Unit || ''}`} />
                    <SummaryCell label="Frequency" value={selectedMed.freq || '—'} />
                    <SummaryCell label="Route"     value={selectedMed.rou || '—'} />
                    <SummaryCell label="Duration"  value={`${selectedMed.day_server || 1} day${selectedMed.day_server == 1 ? '' : 's'}`} />
                  </Box>
                </Paper>

                {/* First-dose pickers */}
                <Paper elevation={0} sx={{ p: 2, border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff', mt: 1.5 }}>
                  <Typography sx={{ ...LABEL_SX, mb: 1 }}>First Dose</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <TextField
                      label="Date"
                      type="date"
                      size="small"
                      value={firstDoseDate}
                      onChange={(e) => setFirstDoseDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="Time"
                      type="time"
                      size="small"
                      value={firstDoseTime}
                      onChange={(e) => setFirstDoseTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Stack>

                  {/* Chart-mode toggle. 'quantity' caps the schedule at the dispensed
                      units — right for tablets/vials where 1 unit = 1 dose.
                      'frequency' schedules the full course (frequency × duration)
                      and ignores the unit count — right for syrups/infusions where
                      one dispensed container carries multiple doses. */}
                  <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ ...LABEL_SX, mb: 0.75 }}>Chart Using</Typography>
                    <ToggleButtonGroup
                      value={chartMode}
                      exclusive
                      size="small"
                      onChange={(_, v) => { if (v) setChartMode(v); }}
                      sx={{
                        width: '100%',
                        '& .MuiToggleButton-root': {
                          flex: 1, py: 0.6, textTransform: 'none',
                          fontWeight: 700, fontSize: '0.78rem',
                          color: MUTED, borderColor: BORDER,
                          '&.Mui-selected': {
                            bgcolor: PRIMARY_BG, color: PRIMARY, borderColor: PRIMARY,
                            '&:hover': { bgcolor: PRIMARY_BG },
                          },
                        },
                      }}
                    >
                      <ToggleButton value="quantity">
                        Quantity ({quantity} unit{quantity === 1 ? '' : 's'})
                      </ToggleButton>
                      <ToggleButton value="frequency">
                        Frequency ({dosesPerDayLabel} × {selectedMed.day_server || 1}d)
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.75 }}>
                      {chartMode === 'frequency'
                        ? 'Scheduling the full course from the prescribed frequency — dispensed quantity is ignored.'
                        : 'Scheduling capped by the dispensed quantity — good for tablets/vials where one unit = one dose.'}
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: '0.72rem', color: MUTED, mt: 1 }}>
                    {(() => {
                      const explicitOffsets = resolveDoseOffsets(selectedMed?.freq);
                      if (explicitOffsets) {
                        return `Doses at ${explicitOffsets.join(', ')} hours from the first dose.`;
                      }
                      if (dosesPerDay <= 0) {
                        return 'Each subsequent dose = first dose + no repeat (PRN / single dose).';
                      }
                      const gap = 24 / dosesPerDay;
                      const gapLabel = gap >= 2 ? gap.toFixed(0) : gap.toFixed(1);
                      return `Each subsequent dose = first dose + ${gapLabel} hours.`;
                    })()}
                  </Typography>
                </Paper>

                {/* Quick stats */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1.25, mt: 1.5,
                }}>
                  <StatTile icon={<AccessTime />}     label="Per day"    value={dosesPerDayLabel} />
                  <StatTile icon={<Inventory2 />}     label="Available"  value={`${quantity} unit${quantity === 1 ? '' : 's'}`} />
                  <StatTile icon={<EventAvailable />} label="To schedule" value={totalEntries} />
                </Box>

                {/* Insufficient warning (inline, compact) */}
                {insufficient && (
                  <Paper elevation={0} sx={{
                    mt: 1.5, p: 1.25, borderRadius: 2,
                    border: '1px solid #fde68a',
                    bgcolor: '#fffbeb',
                    display: 'flex', alignItems: 'flex-start', gap: 1,
                  }}>
                    <WarningAmberRounded sx={{ color: '#A16207', fontSize: 20, mt: 0.25 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400E' }}>
                        Available quantity is less than the full course
                      </Typography>
                      <Typography sx={{ fontSize: '0.76rem', color: '#92400E', mt: 0.25 }}>
                        Only {quantity} dose{quantity === 1 ? '' : 's'} will be scheduled. Top up the dispensed quantity
                        if you need the full {selectedMed.day_server || 1}-day course.
                      </Typography>
                    </Box>
                  </Paper>
                )}

                {/* Schedule preview */}
                <Paper elevation={0} sx={{ mt: 1.5, border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`, bgcolor: '#f8fafc',
                             display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ ...LABEL_SX }}>Schedule preview</Typography>
                    <Chip size="small" label={`${schedule.length} dose${schedule.length === 1 ? '' : 's'}`}
                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: PRIMARY_BG, color: PRIMARY }} />
                  </Box>
                  <TableContainer sx={{ maxHeight: 240 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff', width: 56 }}>#</TableCell>
                          <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Date</TableCell>
                          <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Time</TableCell>
                          <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Day</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schedule.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ color: MUTED, fontSize: '0.8rem', textAlign: 'center', py: 3 }}>
                              No doses to schedule.
                            </TableCell>
                          </TableRow>
                        ) : schedule.map((s, idx) => {
                          const m = moment(s.due_at);
                          return (
                            <TableRow key={`${s.due_at}-${idx}`}>
                              <TableCell sx={{ fontSize: '0.78rem', color: MUTED }}>{idx + 1}</TableCell>
                              <TableCell sx={{ fontSize: '0.84rem', fontWeight: 600, color: STRONG }}>
                                {m.format('ddd D MMM')}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.84rem', fontWeight: 600, color: STRONG }}>
                                {m.format('hh:mm A')}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.78rem', color: MUTED }}>
                                Day {Math.floor(m.diff(moment(`${firstDoseDate} ${firstDoseTime}`, 'YYYY-MM-DD HH:mm'), 'hours') / 24) + 1}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedMed && (
            <Tooltip title="Charted by">
              <Chip size="small" icon={<CheckCircle sx={{ fontSize: 14 }} />} label={currentUser || 'Nurse'}
                sx={{ height: 22, bgcolor: PRIMARY_BG, color: PRIMARY, fontWeight: 700, fontSize: '0.72rem' }} />
            </Tooltip>
          )}
        </Box>
        <Button onClick={onClose} disabled={saving} sx={{ color: MUTED, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !selectedMed || schedule.length === 0}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <ScheduleSend />}
          sx={{
            bgcolor: PRIMARY,
            '&:hover': { bgcolor: PRIMARY_DARK },
            textTransform: 'none', fontWeight: 700, px: 3,
          }}
        >
          {saving ? 'Charting…' : `Chart ${schedule.length || ''} dose${schedule.length === 1 ? '' : 's'}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Local presentational helpers ──────────────────────────────────────
function SummaryCell({ label, value }) {
  return (
    <Box>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: STRONG, mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
}

function StatTile({ icon, label, value }) {
  return (
    <Paper elevation={0} sx={{
      p: 1.25, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff',
      display: 'flex', alignItems: 'center', gap: 1.25,
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1.5,
        bgcolor: PRIMARY_BG, color: PRIMARY,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={LABEL_SX}>{label}</Typography>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}
